/* VETORA content pages — native JS, no dependencies.
   Custom elements re-initialise themselves on Theme Editor section reloads,
   so listeners are never duplicated. */
(function () {
  'use strict';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal (optional, per section) ---------- */
  var revealObserver = null;
  function observeReveals(root) {
    var nodes = (root || document).querySelectorAll('.vetora-reveal:not(.is-visible)');
    if (!nodes.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); revealObserver.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    }
    nodes.forEach(function (n) { revealObserver.observe(n); });
  }
  document.addEventListener('DOMContentLoaded', function () { observeReveals(document); });
  document.addEventListener('shopify:section:load', function (e) { observeReveals(e.target); });

  /* ---------- FAQ accordion group ---------- */
  if (!customElements.get('vetora-faq-group')) {
    customElements.define('vetora-faq-group', class extends HTMLElement {
      connectedCallback() {
        this.items = Array.prototype.slice.call(this.querySelectorAll('.vetora-faq-group__q'));
        this.singleOpen = this.dataset.single !== 'false';
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown.bind(this));
        var openFirst = this.dataset.openFirst !== 'false';
        this.items.forEach(function (q, i) {
          this.setState(q, openFirst && i === 0, true);
        }, this);
      }
      disconnectedCallback() { this.removeEventListener('click', this.onClick); }
      onClick(e) {
        var btn = e.target.closest('.vetora-faq-group__btn');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        this.toggle(btn.closest('.vetora-faq-group__q'));
      }
      onKeydown(e) {
        var btn = e.target.closest('.vetora-faq-group__btn');
        if (!btn) return;
        var btns = this.items.map(function (q) { return q.querySelector('.vetora-faq-group__btn'); });
        var i = btns.indexOf(btn);
        if (e.key === 'ArrowDown') { e.preventDefault(); (btns[i + 1] || btns[0]).focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); (btns[i - 1] || btns[btns.length - 1]).focus(); }
        if (e.key === 'Home') { e.preventDefault(); btns[0].focus(); }
        if (e.key === 'End') { e.preventDefault(); btns[btns.length - 1].focus(); }
      }
      toggle(q) {
        if (!q) return;
        var wasOpen = q.classList.contains('is-open');
        if (this.singleOpen) {
          this.items.forEach(function (other) { if (other !== q) this.setState(other, false); }, this);
        }
        this.setState(q, !wasOpen);
      }
      open(q) { if (q && !q.classList.contains('is-open')) this.toggle(q); }
      setState(q, open, immediate) {
        var btn = q.querySelector('.vetora-faq-group__btn');
        var panel = q.querySelector('.vetora-faq-group__answer');
        if (!btn || !panel) return;
        q.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = false;
        if (immediate || reduceMotion) {
          panel.style.height = open ? 'auto' : '0px';
          if (!open) panel.setAttribute('aria-hidden', 'true'); else panel.removeAttribute('aria-hidden');
          return;
        }
        if (open) {
          panel.removeAttribute('aria-hidden');
          panel.style.height = '0px';
          var target = panel.scrollHeight + 16; // + top padding applied by .is-open
          requestAnimationFrame(function () { panel.style.height = target + 'px'; });
          var done = function (ev) {
            if (ev.propertyName !== 'height') return;
            panel.removeEventListener('transitionend', done);
            if (q.classList.contains('is-open')) panel.style.height = 'auto';
          };
          panel.addEventListener('transitionend', done);
        } else {
          panel.style.height = panel.getBoundingClientRect().height + 'px';
          // force reflow
          void panel.offsetHeight;
          panel.style.height = '0px';
          panel.setAttribute('aria-hidden', 'true');
        }
      }
    });
  }

  /* ---------- FAQ pill tabs (smooth scroll + scroll-spy) ---------- */
  if (!customElements.get('vetora-faq-tabs')) {
    customElements.define('vetora-faq-tabs', class extends HTMLElement {
      connectedCallback() {
        this.links = Array.prototype.slice.call(this.querySelectorAll('.vetora-faq-tabs__link'));
        this.items = Array.prototype.slice.call(this.querySelectorAll('.vetora-faq-tabs__item'));
        this.list = this.querySelector('.vetora-faq-tabs__list');
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.onScroll();
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      sections() {
        return this.links.map(function (l) {
          var href = l.getAttribute('href') || '';
          var id = href.indexOf('#') >= 0 ? href.slice(href.indexOf('#') + 1) : '';
          return id ? document.getElementById(id) : null;
        });
      }
      offset() {
        var sticky = this.closest('.vetora-faq-tabs') || this;
        return (sticky ? sticky.offsetHeight : 0) + 10;
      }
      onClick(e) {
        var link = e.target.closest('.vetora-faq-tabs__link');
        if (!link) return;
        var i = this.links.indexOf(link);
        var target = this.sections()[i];
        if (!target) return; // let a real URL navigate normally
        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.scrollY - this.offset();
        var startY = window.scrollY;
        window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
        if (!reduceMotion) {
          // Fallback: if the smooth animation is suspended (e.g. throttled tab),
          // jump straight to the target so the control always works.
          setTimeout(function () {
            if (Math.abs(window.scrollY - startY) < 2 && Math.abs(startY - top) > 2) {
              window.scrollTo({ top: top, behavior: 'auto' });
            }
          }, 400);
        }
        this.setActive(i);
        if (history.replaceState) history.replaceState(null, '', link.getAttribute('href'));
      }
      onScroll() {
        if (this._ticking) return;
        this._ticking = true;
        var self = this;
        requestAnimationFrame(function () {
          self._ticking = false;
          var middle = window.innerHeight / 2;
          self.sections().forEach(function (s, i) {
            if (!s) return;
            var r = s.getBoundingClientRect();
            if (r.top <= middle && r.bottom >= middle) self.setActive(i);
          });
        });
      }
      setActive(i) {
        if (this._active === i) return;
        this._active = i;
        this.items.forEach(function (it, j) {
          it.classList.toggle('is-active', j === i);
          var a = it.querySelector('.vetora-faq-tabs__link');
          if (a) { if (j === i) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current'); }
        });
        // keep the active pill visible in the horizontally scrolling mobile list
        var item = this.items[i];
        if (item && this.list && this.list.scrollWidth > this.list.clientWidth) {
          var lr = this.list.getBoundingClientRect(), ir = item.getBoundingClientRect();
          var delta = (ir.left + ir.width / 2) - (lr.left + lr.width / 2);
          this.list.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    });
  }

  /* ---------- Theme Editor: open the selected question block ---------- */
  document.addEventListener('shopify:block:select', function (e) {
    var q = e.target && e.target.closest ? e.target.closest('.vetora-faq-group__q') : null;
    if (!q) return;
    var group = q.closest('vetora-faq-group');
    if (group && group.open) group.open(q);
  });
})();
